import React from 'react';
import { format } from 'date-fns';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';

const UsersTable = ({ users, isLoading, isFetching, totalRecords, page, totalPages, onPageChange, onRowClick }) => {
  return (
    <Card className="overflow-hidden border-none shadow-2xl shadow-surface-200/50 rounded-[2rem] bg-white">
      <div className="p-6 border-b border-surface-50 flex items-center justify-between bg-surface-50/30">
        <p className="text-[12px] text-surface-400 font-bold uppercase tracking-wider">
          Total Records: {totalRecords.toLocaleString()}
        </p>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold text-surface-400 uppercase tracking-widest italic">Click row for full profile</span>
          <span className={`w-2 h-2 rounded-full ${isFetching ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'}`} />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-50/50">
              {['Outlet Name', 'Mobile No.', 'Slab', 'Gift', 'Pincode', 'State', 'Last Updated'].map(h => (
                <th key={h} className="px-6 py-5 text-left text-[10px] font-black text-surface-400 uppercase tracking-[0.2em] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-50">
            {isLoading ? (
              [...Array(8)].map((_, i) => (
                <tr key={i}><td colSpan="7" className="px-6 py-4"><Skeleton className="h-12 w-full rounded-2xl" /></td></tr>
              ))
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-32 text-center text-surface-400 font-medium italic">No users found for this scheme.</td>
              </tr>
            ) : (
              users.map((user, idx) => (
                <tr key={idx} onClick={() => onRowClick(user.mobile_no)} className="hover:bg-primary-50/30 transition-all duration-300 group cursor-pointer">
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-surface-100 flex items-center justify-center text-surface-400 font-bold text-xs group-hover:bg-primary-100 group-hover:text-primary-600 shrink-0">
                        {(user.updated_outlet_name || user.outlet_name)?.charAt(0) || '?'}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="font-bold text-surface-800 text-sm tracking-tight leading-tight truncate max-w-[160px]">
                          {user.updated_outlet_name || user.outlet_name || '—'}
                        </span>
                        {user.updated_outlet_name && (
                          <span className="text-[9px] text-surface-400 font-bold italic truncate max-w-[160px]">orig: {user.outlet_name}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-[11px] font-mono font-black text-primary-600 bg-primary-50 px-3 py-1.5 rounded-xl border border-primary-100/50">
                      {user.mobile_no}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-[10px] font-black uppercase tracking-tighter text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-100">
                      {user.achieved_slab || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    {user.selected_gift
                      ? <Badge variant="accent" className="font-black text-[9px] uppercase px-3 py-1 rounded-lg">{user.selected_gift}</Badge>
                      : <span className="text-[10px] font-bold text-surface-300 uppercase italic">Pending</span>
                    }
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-xs font-bold text-surface-600">{user.pincode || '—'}</span>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-xs font-bold text-surface-800">{user.state || '—'}</span>
                  </td>
                  <td className="px-6 py-5 text-surface-400 font-bold text-[11px] uppercase tracking-tighter whitespace-nowrap">
                    {user.updated_at ? format(new Date(user.updated_at), 'MMM dd, yyyy') : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-8 py-6 border-t border-surface-50 bg-surface-50/20">
        <div className="flex items-center space-x-2">
          <span className="text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">Page</span>
          <div className="bg-white border border-surface-200 rounded-lg px-2 py-1 text-xs font-black text-primary-600">{page} / {totalPages}</div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={() => onPageChange(p => Math.max(1, p - 1))} disabled={page === 1 || isLoading} className="h-11 px-4 rounded-xl border-surface-200 text-xs font-bold">Previous</Button>
          <Button variant="outline" onClick={() => onPageChange(p => p + 1)} disabled={page >= totalPages || isLoading} className="h-11 px-4 rounded-xl border-surface-200 text-xs font-bold">Next</Button>
        </div>
      </div>
    </Card>
  );
};

export default UsersTable;